import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, Optional, Inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { AlertService } from 'src/app/services/alert.service';
import { UserService } from 'src/app/services/user.service';
import { KilnService } from 'src/app/services/kiln.service';
import { AlertController, NavController, PickerController, Platform, IonSelect, ToastController } from '@ionic/angular';
import { PushNotificationService, PushSetupStatus } from 'src/app/services/push-notification.service';
import { ToggleService } from 'src/app/services/toggle.service';
import { TranslateService } from '@ngx-translate/core';
import { scrollFocusedFieldIntoView } from 'src/app/utils/scroll-focused-field';

import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'app-account-settings-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class AccountSettingsUsersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() small_screen = false;
  @ViewChild('userSelect', { static: false }) userSelect: IonSelect;

  private resizeSub: any;

  @Output() pending = new EventEmitter<Object>();
  @Output() buttonText = new EventEmitter<Object>();

  public usersForm: UntypedFormGroup;
  public phonesForm: UntypedFormGroup;
  public emailForm: UntypedFormGroup;
  public kilnSecurityForm: UntypedFormGroup;

  public selected_tab = 'details';
  public selected_user = null;
  public selected_index = null;
  public updatePending = false;
  public deletePending = false;

  public pushDeviceName = '';
  public pushRegistering = false;
  public otherUserPushDevices: Array<{ id: number; name?: string; platform?: string; created_at?: string }> = [];
  public otherUserPushLoading = false;
  public promptPushSending = false;
  public pushTestingDeviceId: number | null = null;
  public pushSetupStatus: PushSetupStatus | null = null;
  public pushPromptPending = false;
  /** Whether the current device can register an additional push token for the selected user. */
  public canRegisterAdditionalPushDevice = false;

  public hideAdmins = false;
  public hideBasics = false;
  public hideReadOnly = false;

  public network_devices = [];
  public kilns_by_network_device = {};
  public allowUsers = "all";

  public gettingUserData = false;
  public all_kilns = [];
  public phone_type_options = ['Home', 'Mobile', 'Work'];
  private phoneUtil;
  private loggedInUser;
  private selected_user_email;
  private is_changing_email = false;
  public userInfo = {admin: [], basic: [], readOnly: []};
  public showSecondaryPhoneNumber = false;
  public showPrimaryPhoneNumber = true;
  public showPushNotificationsSection = true;

  /** Small-screen Devices tab: one section at a time (push / primary phone / secondary phone). */
  public editingNotificationSection: 'push' | 'primary_phone' | 'secondary_phone' | null = null;

  roleFilters = {
    admin: true,
    basic: true,
    readonly: true
  };

  selectedRole: 'all' | 'admin' | 'basic' | 'readonly' = 'all';

  /** Whether push notifications feature is enabled (pushNotifications toggle). */
  public showPushNotifications = false;

  /** Whether stop (stop firing) permission is accessible (stop toggle). */
  public showStopToggle = false;

  /** Whether programming permission is accessible (programming toggle). */
  public showProgrammingToggle = false;

  /** True when opened with `add=user`: show create-user form immediately and skip default user selection after load. */
  public createUserFormDuringAccountLoad = false;

  /** True while the create-user form is open (from landing `add=user` or in-page Add User). */
  public isAddingNewUser = false;

  /**
   * Small-screen admin: after loading users, no row is selected until the user picks one or taps Add user.
   * While true, submit is disabled and save() no-ops to avoid accidental creates.
   */
  public smallScreenAwaitingUserPick = false;

  /** After save on small screen, re-open the same user/tab once refreshed user list returns. */
  private smallScreenReselectAfterRefresh: {
    userId?: number;
    email?: string;
    username?: string;
    tab?: string;
    notificationSection?: 'push' | 'primary_phone' | 'secondary_phone';
  } | null = null;

  /** Small-screen admin: hide tabs/forms until a user is chosen from the list or Add user is used. */
  get showUserAccountDetailsPanel(): boolean {
    return !this.small_screen || !this.smallScreenAwaitingUserPick;
  }

  /**
   * Small-screen admin: leave the editor and return to the user picker (discards add-user flow or current selection).
   */
  cancelSmallScreenUserEditor(): void {
    if (!this.small_screen || !this.canAdminister || this.smallScreenAwaitingUserPick) {
      return;
    }
    this.exitAddUserMode();
  }

  /** Scroll focused form fields above the keyboard and fixed footer on small screens. */
  scrollFormFieldIntoView(event: FocusEvent): void {
    scrollFocusedFieldIntoView(event, { enabled: this.small_screen });
  }

  /** Leave create-user mode and return to the normal user list / picker. */
  exitAddUserMode(): void {
    this.isAddingNewUser = false;
    this.createUserFormDuringAccountLoad = false;
    this.clearAddUserQueryParam();
    if (this.small_screen) {
      this.resetSmallScreenToUserPicker();
    } else {
      this.applyDefaultUserSelectionAfterLoad();
    }
  }

  private resetSmallScreenToUserPicker(): void {
    this.selected_user = null;
    this.selected_index = null;
    this.selected_tab = 'details';
    this.editingNotificationSection = null;
    this.smallScreenAwaitingUserPick = true;
    this.isAddingNewUser = false;
  }

  /**
   * After the account user list loads, auto-select when only one user is available
   * (e.g. Basic/Readonly viewing self, or an account with a single user).
   */
  private applyDefaultUserSelectionAfterLoad(): void {
    const users = this.filteredUsers;
    if (users.length === 1) {
      this.selectUser(users[0], 0);
      return;
    }

    if (this.canAdminister) {
      if (this.small_screen) {
        this.resetSmallScreenToUserPicker();
      } else if (users.length > 0) {
        const idx = this.indexOfLoggedInUserInList(users);
        const pick = idx >= 0 ? idx : 0;
        this.selectUser(users[pick], pick);
      }
    } else if (users.length > 0) {
      this.selectUser(users[0], 0);
    }
  }

  private queueSmallScreenReselect(
    user: { id?: number; email?: string; username?: string },
    tab?: string,
    notificationSection?: 'push' | 'primary_phone' | 'secondary_phone' | null
  ): void {
    if (!this.small_screen || !user) {
      return;
    }
    this.smallScreenReselectAfterRefresh = {
      userId: user.id,
      email: user.email,
      username: user.username,
      tab: tab || this.selected_tab,
      notificationSection: notificationSection ?? this.editingNotificationSection ?? undefined,
    };
  }

  private findUserIndexInList(
    users: any[],
    ref: { userId?: number; email?: string; username?: string }
  ): number {
    if (!users?.length || !ref) {
      return -1;
    }
    if (ref.userId != null) {
      const byId = users.findIndex((user) => user.id === ref.userId);
      if (byId >= 0) {
        return byId;
      }
    }
    if (ref.email) {
      const byEmail = users.findIndex((user) => user.email === ref.email || user.mail_to === ref.email);
      if (byEmail >= 0) {
        return byEmail;
      }
    }
    if (ref.username) {
      return users.findIndex((user) => user.username === ref.username);
    }
    return -1;
  }

  private async showSaveFeedback(message: string, success: boolean): Promise<void> {
    this.pending.emit({ is_pending: false, message, success });
    try {
      const toast = await this.toastController.create({
        message,
        duration: 4000,
        position: 'bottom',
        color: success ? 'success' : 'danger',
      });
      await toast.present();
    } catch (error) {
      console.warn('Failed to present save feedback toast', error);
    }
  }

  /** KilnLink premium account (phone / push tabs are premium-only). */
  isPremiumAccount(): boolean {
    return this.userService.isPremium();
  }

  /** Account admin enabled SMS/text notifications for this account. */
  get accountSmsEnabled(): boolean {
    return !!this.accountInformation?.general?.sms_notifications_enabled;
  }

  /** Account admin enabled push notifications for this account (defaults true when unset). */
  get accountPushEnabled(): boolean {
    return this.accountInformation?.general?.push_notifications_enabled !== false;
  }

  /** Push device UI when feature toggle and account setting both allow push. */
  get showPushDeviceManagement(): boolean {
    return this.showPushNotifications && this.accountPushEnabled;
  }

  /** If account is not premium, leave notifications/email tabs (not offered in UI). */
  private ensurePremiumTabVisibility(): void {
    if (this.isPremiumAccount()) {
      return;
    }
    if (this.selected_tab === 'notifications' || this.selected_tab === 'phone' || this.selected_tab === 'push' || this.selected_tab === 'email') {
      this.selected_tab = 'details';
    }
  }

  /** Map legacy tab query values to unified notifications tab. */
  private normalizeTabName(tab: string): string {
    if (tab === 'phone' || tab === 'push') {
      return 'notifications';
    }
    return tab;
  }

  constructor(public accountService: AccountService,
    public alertService: AlertService,
    public userService: UserService,
    public kilnService: KilnService,
    public alertController: AlertController,
    public navCtrl: NavController,
    private toastController: ToastController,
    private pickerController: PickerController,
    private platform: Platform,
    private pushNotificationService: PushNotificationService,
    private toggleService: ToggleService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean
  ) {
    let login_data = this.userService.getLoginData();
    let loggedInAs = login_data.loggedInAs;
    this.loggedInUser = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? (loggedInAs.email || loggedInAs.username) : (login_data.email || login_data.username);
    this.phoneUtil = PhoneNumberUtil.getInstance();    
  }

  getUserAndDeviceInformation(preserveSmallScreenEditor = false) {
    if (preserveSmallScreenEditor && this.small_screen && this.selected_user) {
      this.queueSmallScreenReselect(this.selected_user, this.selected_tab);
    }
    this.gettingUserData = true;
    this.getAccountUsers();
    this.accountService.getDevicesForAccount().then( (data) => {
      this.network_devices = [];
      this.all_kilns = [];
      this.kilns_by_network_device = {};
      this.network_devices = data && data.network_devices;
      let kilns = data && data.kilns.length > 0 ? data.kilns.sort((a, b) => (a.name > b.name) ? 1 : -1) : [];
      this.all_kilns = kilns;

      if(kilns) {
        kilns = kilns.filter((k) => k)
        this.kilns_by_network_device = kilns.reduce((accumulator, val) => {
          let network_device_id = val.network_device_id || '-1';

          if(accumulator.hasOwnProperty(network_device_id)) {
            accumulator[network_device_id].push(val);
          } else {
            accumulator[network_device_id] = [val];
          }

          return accumulator;
        }, {})
      } else {
        this.kilns_by_network_device = {};
      }

      this.kilnSecurityForm = undefined;
      this.createKilnSecurityForm();
    });
  }

  ngOnInit() {
    this.toggleService.listToggles();
    this.toggleService.togglesLoaded.subscribe(() => {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
      this.showStopToggle = this.toggleService.isToggleEnabled('stop');
      this.showProgrammingToggle = this.toggleService.isToggleEnabled('programming') && this.userService.isPremium();
      this.ensurePremiumTabVisibility();
    this.applyTabFromRoute();
    });
    if (this.toggleService.areTogglesLoaded()) {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
      this.showStopToggle = this.toggleService.isToggleEnabled('stop');
      this.showProgrammingToggle = this.toggleService.isToggleEnabled('programming') && this.userService.isPremium();
    }
    this.is_changing_email = false;
    this.network_devices = [];
    this.all_kilns = [];
    this.kilns_by_network_device = {};
    let login_data = this.userService.getLoginData();
    let loggedInAs = login_data.loggedInAs;
    this.loggedInUser = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? (loggedInAs.email || loggedInAs.username) : (login_data.email || login_data.username);
    this.gettingUserData = true;
    this.selected_user = null;
    // Detect small screen so user list shows as ion-select when embedded (e.g. via ngComponentOutlet)
    this.updateSmallScreen();
    this.resizeSub = this.platform.resize.subscribe(() => this.updateSmallScreen());

    const openCreateUserFromRoute =
      this.canAdminister && this.urlSaysAddUserIntent();

    if (openCreateUserFromRoute) {
      this.createUserFormDuringAccountLoad = true;
      this.addUser();
    } else {
      this.createForm();
      this.createPhonesForm();
      this.createEmailForm();
    }
    this.ensurePremiumTabVisibility();
    this.applyTabFromRoute();
    this.getUserAndDeviceInformation();
  }

  /** `add=user` may not appear on this component's ActivatedRoute snapshot when embedded in Account Admin; read from the real URL. */
  private urlSaysAddUserIntent(): boolean {
    try {
      const q = this.router.parseUrl(this.router.url).queryParams['add'];
      return q === 'user';
    } catch {
      return this.route.snapshot.queryParamMap.get('add') === 'user';
    }
  }

  /** Open notifications tab when navigated with ?tab=users&section=notifications (or legacy phone/push). */
  private applyTabFromRoute(): void {
    if (!this.isPremiumAccount()) {
      return;
    }
    try {
      const params = this.router.parseUrl(this.router.url).queryParams;
      const section = params['section'] || params['userTab'];
      const tab = params['tab'];
      if (tab === 'users' && (section === 'notifications' || section === 'phone' || section === 'push')) {
        this.selected_tab = 'notifications';
        if (this.showPushNotifications) {
          this.onNotificationsTabSelected();
        }
      }
    } catch {
      const section = this.route.snapshot.queryParamMap.get('section');
      if (section === 'notifications' || section === 'phone' || section === 'push') {
        this.selected_tab = 'notifications';
        if (this.showPushNotifications) {
          this.onNotificationsTabSelected();
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.resizeSub) {
      this.resizeSub.unsubscribe();
    }
  }

  private updateSmallScreen() {
    const w = this.platform?.width() ?? 0;
    const isDevice = this.platform?.is('mobile') || this.platform?.is('tablet') || this.platform?.is('phablet') || this.platform?.is('mobileweb') || this.platform?.is('desktop');
    this.small_screen = w < 875 && !!isDevice;
  }

  ngOnChanges() {
    this.is_changing_email = false;
    this.ensurePremiumTabVisibility();
    this.applyTabFromRoute();
    this.getUserAndDeviceInformation();
  }

  verificationSuccess() {
    if (this.small_screen && this.selected_user) {
      this.queueSmallScreenReselect(this.selected_user, this.selected_tab);
    }
    this.getAccountUsers();
    this.pending.emit({is_pending: false,message: "Please check for your device for a code to complete device verification.",success: true});
  }
  verificationFails() {this.pending.emit({is_pending: false,message: "Something went wrong sending your device a verification code",success: false});}

  deviceConfirmSuccess() {
    this.getAccountUsers();
    this.pending.emit({is_pending: false,message: "Your device has been verified",success: true});
  }
  deviceConfirmFails() {
    this.pending.emit({is_pending: false,message: "Your device could not be verified at this time", success: false});
  }

  getAccountUsers() {
    this.accountService.getUsersForAccount()
        .then((response) => {
          this.accountInformation.users = response.sort((a, b) => {
            return (a.email > b.email) ? 1 : -1;
          });
          this.setUpCanAdminister(response);
          this.userInfo = {
            admin: this.admins(),
            basic: this.basicUsers(),
            readOnly: this.readOnlyUsers()
          };

          const keepNewUserForm = this.createUserFormDuringAccountLoad;
          if (keepNewUserForm) {
            this.createUserFormDuringAccountLoad = false;
            this.gettingUserData = false;
            // Keep `add=user` in the URL while the create form is open (drives Account Admin title/hint).
            return;
          }

          if (this.canAdminister) {
            if (this.small_screen) {
              const reselect = this.smallScreenReselectAfterRefresh;
              this.smallScreenReselectAfterRefresh = null;
              if (reselect) {
                const users = this.accountInformation.users;
                const idx = this.findUserIndexInList(users, reselect);
                if (idx >= 0) {
                  this.selectUser(users[idx], idx, {
                    tab: reselect.tab || 'details',
                    notificationSection: reselect.notificationSection,
                  });
                } else {
                  this.applyDefaultUserSelectionAfterLoad();
                }
              } else {
                this.applyDefaultUserSelectionAfterLoad();
              }
            } else {
              this.applyDefaultUserSelectionAfterLoad();
            }
          } else {
            this.accountInformation.users = this.accountInformation.users.filter((user) => {
              return user.email === this.loggedInUser || user.username === this.loggedInUser;
            });
            this.applyDefaultUserSelectionAfterLoad();
          }

          this.ensurePremiumTabVisibility();
    this.applyTabFromRoute();
          this.gettingUserData = false;
        })
        .catch((error) => {
          this.gettingUserData = false;
          console.log(`error getting account users: ${error}`)
        })
  }

  /** Drop `add=user` from the URL after the new-user form is established (avoids re-selecting on refresh). */
  private clearAddUserQueryParam(): void {
    if (!this.urlSaysAddUserIntent()) {
      return;
    }
    this.router.navigate(['/account-admin'], {
      queryParams: { add: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Index of the signed-in user in `users` (for default selection when role filter is “all”). */
  private indexOfLoggedInUserInList(users: any[]): number {
    if (!users || !this.loggedInUser) {
      return -1;
    }
    return users.findIndex((u) =>
      u.email ? (u.email === this.loggedInUser || u.mail_to === this.loggedInUser) : (u.username === this.loggedInUser)
    );
  }

  setUpCanAdminister(data) {
    if(this.accountInformation && this.accountInformation.users && this.loggedInUser) {
      let loggedInUserPermissions = this.accountInformation.users.find((u) => {
        return u.email ? (u.email === this.loggedInUser || u.mail_to === this.loggedInUser) : (u.username === this.loggedInUser);
      })

      this.canAdminister = loggedInUserPermissions ? (loggedInUserPermissions.type === 'admin') : false;
    } else {
      this.canAdminister = false;
    }
  }

  private createForm(user?) {
    let emptyFormControl = (default_value?) => { return new UntypedFormControl((default_value === undefined ? '' : default_value), [Validators.required, Validators.minLength(2)]); }
    let emptyFormControlNotRequired = (default_value?) => { return new UntypedFormControl(((default_value === undefined) ? "" : default_value)); }

    this.selected_user_email = user && user.email;
    this.usersForm = new UntypedFormGroup({
      email: emptyFormControl(user && user.email),
      verification_code: new UntypedFormControl(''),
      type: emptyFormControl((user && user.type) || 'basic'),
      first_name: emptyFormControl(user && user.first_name),
      last_name: emptyFormControl(user && user.last_name)
    });

    if(!user) {
      this.buttonText.emit({submitButton: "Create"});
    }
  }

  clearCurrentForm() {
    let getFormToCheck = {
      "details": this.usersForm,
      "phone": this.phonesForm,
      "email": this.emailForm,
      "security": this.kilnSecurityForm
    }

    let currentForm = getFormToCheck[this.selected_tab];
    currentForm.reset();
  }

  private createPhonesForm() {
    let primary = this.getPhoneInformation();
    let secondary = this.getPhoneInformation(true);

    let isPhoneNumber = input => {
      try {
        let ISD_CODES = [93, 355, 213, 1684, 376, 244, 1264, 672, 1268, 54, 374, 297, 61, 43, 994, 1242, 973, 880, 1246, 375, 32, 501, 229, 1441, 975, 591, 387, 267, 55, 246, 1284, 673, 359, 226, 257, 855, 237, 1, 238, 1345, 236, 235, 56, 86, 61, 61, 57, 269, 682, 506, 385, 53, 599, 357, 420, 243, 45, 253, 1767, 1809, 1829, 1849, 670, 593, 20, 503, 240, 291, 372, 251, 500, 298, 679, 358, 33, 689, 241, 220, 995, 49, 233, 350, 30, 299, 1473, 1671, 502, 441481, 224, 245, 592, 509, 504, 852, 36, 354, 91, 62, 98, 964, 353, 441624, 972, 39, 225, 1876, 81, 441534, 962, 7, 254, 686, 383, 965, 996, 856, 371, 961, 266, 231, 218, 423, 370, 352, 853, 389, 261, 265, 60, 960, 223, 356, 692, 222, 230, 262, 52, 691, 373, 377, 976, 382, 1664, 212, 258, 95, 264, 674, 977, 31, 599, 687, 64, 505, 227, 234, 683, 850, 1670, 47, 968, 92, 680, 970, 507, 675, 595, 51, 63, 64, 48, 351, 1787, 1939, 974, 242, 262, 40, 7, 250, 590, 290, 1869, 1758, 590, 508, 1784, 685, 378, 239, 966, 221, 381, 248, 232, 65, 1721, 421, 386, 677, 252, 27, 82, 211, 34, 94, 249, 597, 47, 268, 46, 41, 963, 886, 992, 255, 66, 228, 690, 676, 1868, 216, 90, 993, 1649, 688, 1340, 256, 380, 971, 44, 1, 598, 998, 678, 379, 58, 84, 681, 212, 967, 260, 263],
          //extract numbers from string
          thenum = input.match(/[0-9]+/g).join(""),
          totalnums = thenum.length,
          last10Digits = parseInt(thenum) % 10000000000,
          ISDcode = thenum.substring(0, totalnums - 10);

        //phone numbers are generally of 8 to 16 digits
        if (totalnums >= 8 && totalnums <= 16) {
          if (ISDcode) {
            if (ISD_CODES.includes(parseInt(ISDcode))) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        }
      } catch (e) {}

      return false;
    }

    let validatePhoneNumber = (num) => {
      if(num && num.value && num.value !== "") {
        try {
          if(!num.value.includes("+")) { num.value = `+${num.value}`}
          const number = this.phoneUtil.parseAndKeepRawInput(num.value);
          return this.phoneUtil.isValidNumber(number);
        } catch(e) {
          // console.log(`error formatting phone number: ${JSON.stringify(e)}`)
          return {invalidNumber: {value: num.value}};
        }
      } else {
        return num.value !== "" ? {invalidNumber: {value: num.value}} : null;
      }
    }

    let emptyFormControl = (default_value, additional_validators?) => {
      let validators = [];
      if(additional_validators) {
        validators.push(additional_validators);
      }

      return new UntypedFormControl(((default_value === undefined) ? '' : default_value), validators);
    }

    this.phonesForm = new UntypedFormGroup({
      primary_number: emptyFormControl(primary.call_number, validatePhoneNumber),
      primary_type: emptyFormControl(primary.type),
      primary_alerts_enabled: emptyFormControl(this.normalizeAlertsEnabled(primary.enable_for_alerts)),
      secondary_number: emptyFormControl(secondary.call_number, validatePhoneNumber),
      secondary_type: emptyFormControl(secondary.type || 'Mobile'),
      secondary_alerts_enabled: emptyFormControl(this.normalizeAlertsEnabled(secondary.enable_for_alerts)),
      verification_code: emptyFormControl('')
    });
    this.syncSecondaryPhoneVisibility();
    if (this.selected_tab === 'notifications') {
      this.syncNotificationSectionsVisibility();
    }
  }

  private createEmailForm() {
    let email = new UntypedFormControl((this.selected_user && this.selected_user.email) || '', [Validators.required]);
    this.emailForm = new UntypedFormGroup({
      email: email,
      verification_code: new UntypedFormControl('')
    })
  }

  public kilnSecurityFormNetworkDevices() {
    if(this.kilnSecurityForm) {
      return (this.kilnSecurityForm.get('network_devices') as UntypedFormArray) ? (this.kilnSecurityForm.get('network_devices') as UntypedFormArray).controls : [];
    } else {
      return [];
    }
  }

  public getKilnsForNetworkDevice(index) {
    let nds = this.kilnSecurityFormNetworkDevices();
    if(nds.length > 0) {
      return (nds[index].get('kilns') as UntypedFormArray) ? (nds[index].get('kilns') as UntypedFormArray).controls : [];
    } else { return []; }
  }

  onNetworkDeviceViewChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_view')?.setValue(checked);
        });
      }
    }
  }

  onNetworkDeviceStopChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_stop')?.setValue(checked);
        });
      }
    }
  }

  onNetworkDeviceProgrammingChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_program')?.setValue(checked);
        });
      }
    }
  }

  toggleCheckbox(event: Event, networkIndex: number, controlName: string, type: string) {
    // Prevent double-toggling if clicking directly on checkbox
    if ((event.target as HTMLElement).tagName === 'ION-CHECKBOX') {
      return;
    }
    
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let currentValue = networkDevice.get(controlName)?.value;
      networkDevice.get(controlName)?.setValue(!currentValue);
      
      // Trigger the cascade effect
      if(type === 'view') {
        this.onNetworkDeviceViewChange(networkIndex, !currentValue);
      } else if(type === 'stop') {
        this.onNetworkDeviceStopChange(networkIndex, !currentValue);
      } else if(type === 'programming') {
        this.onNetworkDeviceProgrammingChange(networkIndex, !currentValue);
      }
    }
  }

  toggleKilnCheckbox(event: Event, networkIndex: number, kilnIndex: number, controlName: string) {
    // Prevent double-toggling if clicking directly on checkbox
    if ((event.target as HTMLElement).tagName === 'ION-CHECKBOX') {
      return;
    }
    
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray && kilnsArray.controls[kilnIndex]) {
        let kilnControl = kilnsArray.controls[kilnIndex];
        let currentValue = kilnControl.get(controlName)?.value;
        kilnControl.get(controlName)?.setValue(!currentValue);
        
        // Update network device checkbox based on all kilns
        this.updateNetworkDeviceFromKilns(networkIndex, controlName);
      }
    }
  }

  onKilnCheckboxChange(networkIndex: number, controlName: string) {
    // Called when a kiln checkbox changes via ionChange
    this.updateNetworkDeviceFromKilns(networkIndex, controlName);
  }

  updateNetworkDeviceFromKilns(networkIndex: number, controlName: string) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray && kilnsArray.controls.length > 0) {
        // Check if all kilns have this permission checked
        let allChecked = kilnsArray.controls.every((kilnControl) => {
          return kilnControl.get(controlName)?.value === true;
        });
        
        // Update network device checkbox to match
        networkDevice.get(controlName)?.setValue(allChecked);
      } else {
        // No kilns, so uncheck network device
        networkDevice.get(controlName)?.setValue(false);
      }
    }
  }

  /** Kilns not linked to a network device (form group id "-1"); no network-device header row. */
  private static readonly UNCONNECTED_NETWORK_DEVICE_ID = '-1';

  isUnconnectedKilnsSection(networkDev: { value?: { id?: string | number } }): boolean {
    return String(networkDev?.value?.id) === AccountSettingsUsersComponent.UNCONNECTED_NETWORK_DEVICE_ID;
  }

  private getNetworkDevicesForSecurityForm(): any[] {
    const devices = [...(this.network_devices || [])];
    const unconnectedId = AccountSettingsUsersComponent.UNCONNECTED_NETWORK_DEVICE_ID;
    const unconnectedKilns = this.kilns_by_network_device?.[unconnectedId];
    if (
      unconnectedKilns?.length &&
      !devices.some((nd) => String(nd?.id) === unconnectedId)
    ) {
      devices.push({ id: unconnectedId, name: '', can_stop: false });
    }
    return devices;
  }

  private createKilnSecurityForm() {
    this.kilnSecurityForm = undefined;
    if(this.network_devices && this.kilns_by_network_device && this.selected_user) {
      let user_has_access_to_whole_account = (this.selected_user.network_devices && this.selected_user.network_devices.every((nd) => !nd.id)) && (this.selected_user.kilns && this.selected_user.kilns.every((k) => !k.id))
      // Get existing kiln IDs that user can stop (from saved user permissions)
      let existing_stop_kiln_ids = [];
      if(this.selected_user && this.selected_user.can_stop_kilns) {
        if(Array.isArray(this.selected_user.can_stop_kilns)) {
          existing_stop_kiln_ids = this.selected_user.can_stop_kilns.map((k) => {
            if(typeof k === 'object' && k.id) {
              return typeof k.id === 'string' ? parseInt(k.id) : k.id;
            } else {
              return typeof k === 'string' ? parseInt(k) : k;
            }
          }).filter((id) => !isNaN(id));
        }
      }

      // When user has no saved can_stop_kilns, default from API (can_stop on each kiln/ND from getDevicesForAccount)
      const useApiCanStop = existing_stop_kiln_ids.length === 0;

      // Get existing kiln IDs that user can program (from saved user permissions)
      let existing_program_kiln_ids: number[] = [];
      if (this.selected_user && this.selected_user.can_program_kilns && Array.isArray(this.selected_user.can_program_kilns)) {
        existing_program_kiln_ids = this.selected_user.can_program_kilns.map((k: { id?: number } | number) => {
          if (typeof k === 'object' && k && 'id' in k) return typeof k.id === 'string' ? parseInt(k.id) : k.id;
          return typeof k === 'string' ? parseInt(k) : k;
        }).filter((id: number) => !isNaN(id));
      }
      const useApiCanProgram = existing_program_kiln_ids.length === 0;
      const hasCanProgramFromApi = (k: { can_program?: boolean }) => !!(k as { can_program?: boolean }).can_program;

      // Create unified form structure with view, stop, and programming permissions
      let network_device_form_groups = this.getNetworkDevicesForSecurityForm().map((nd) => {
        if(nd) {
          let user_has_access_to_nd = this.selected_user && this.selected_user.network_devices && this.selected_user.network_devices.find((user_accessible_nd) => { return user_accessible_nd.id === nd.id })
          let kilns_to_map = this.kilns_by_network_device[nd.id] || [];
          
          // Check if all kilns under this network device can be viewed
          let all_kilns_can_view = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let user_has_access_to_kiln = this.selected_user && this.selected_user.kilns && this.selected_user.kilns.find((user_accessible_k) => { return user_accessible_k.id === k.id })
            return user_has_access_to_whole_account || (user_has_access_to_kiln !== undefined);
          });
          
          // Check if all kilns under this network device can be stopped (saved ids or API default)
          let all_kilns_can_stop = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
            if (useApiCanStop) return k.can_stop === true;
            return existing_stop_kiln_ids.includes(kilnId);
          });
          
          // Check if all kilns under this network device can be programmed
          let all_kilns_can_program = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
            if (useApiCanProgram) return hasCanProgramFromApi(k);
            return existing_program_kiln_ids.includes(kilnId);
          });
          
          return new UntypedFormGroup({
            name: new UntypedFormControl(nd.name || `Name Unavailable (MAC: ${nd.mac_address})`),
            id: new UntypedFormControl(nd.id),
            can_view: new UntypedFormControl(user_has_access_to_whole_account || (user_has_access_to_nd !== undefined && all_kilns_can_view)),
            can_stop: new UntypedFormControl(useApiCanStop ? (nd.can_stop === true) : all_kilns_can_stop),
            can_program: new UntypedFormControl(useApiCanProgram ? hasCanProgramFromApi(nd) : all_kilns_can_program),
            kilns: new UntypedFormArray(kilns_to_map.map((k) => {
              let user_has_access_to_kiln = this.selected_user && this.selected_user.kilns && this.selected_user.kilns.find((user_accessible_k) => { return user_accessible_k.id === k.id })
              let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
              let can_stop_kiln = useApiCanStop ? (k.can_stop === true) : existing_stop_kiln_ids.includes(kilnId);
              let can_program_kiln = useApiCanProgram ? hasCanProgramFromApi(k) : existing_program_kiln_ids.includes(kilnId);
              
              return new UntypedFormGroup({
                name: new UntypedFormControl(k.name),
                id: new UntypedFormControl(k.id),
                can_view: new UntypedFormControl(user_has_access_to_whole_account || (user_has_access_to_kiln !== undefined)),
                can_stop: new UntypedFormControl(can_stop_kiln),
                can_program: new UntypedFormControl(can_program_kiln)
              });
            }))
          })
        } else {
          return new UntypedFormGroup({
            name: new UntypedFormControl("Not Available"),
            id: new UntypedFormControl("na"),
            can_view: new UntypedFormControl(false),
            can_stop: new UntypedFormControl(false),
            can_program: new UntypedFormControl(false),
            kilns: new UntypedFormArray([])
          })
        }
      });

      this.kilnSecurityForm = new UntypedFormGroup({
        network_devices: new UntypedFormArray(network_device_form_groups)
      })
    } else {
      this.kilnSecurityForm = undefined;
    }
  }

  basicUsers() {
    if(!this.accountInformation) { return []; } else {
      if(this.canAdminister) {
        return this.accountInformation.users.filter((user) => { return user.type === 'basic' || !user.type });
      } else {
        return this.accountInformation.users.filter((user) => { return (user.email === this.loggedInUser || user.username === this.loggedInUser) && user.type === 'basic'});
      }
    }
  }

  admins() {
    return this.accountInformation ? this.accountInformation.users.filter((user) => { return user.type === 'admin'}) : [];
  }

  readOnlyUsers() {
    if(!this.accountInformation) { return []; } else {
      if(this.canAdminister) {
        return this.accountInformation.users.filter((user) => { return user.type === 'readonly' || !user.type });
      } else {
        return this.accountInformation.users.filter((user) => { return (user.email === this.loggedInUser || user.username === this.loggedInUser) && user.type === 'readonly'});
      }
    }
  }

  selectUser(
    user,
    index,
    options?: {
      tab?: string;
      notificationSection?: 'push' | 'primary_phone' | 'secondary_phone';
    }
  ) {
    this.isAddingNewUser = false;
    this.clearAddUserQueryParam();
    this.smallScreenAwaitingUserPick = false;
    this.selected_tab = options?.tab ?? 'details';
    this.editingNotificationSection = null;
    this.selected_user = user;
    this.selected_index = index;
    this.createForm(this.selected_user)
    this.createPhonesForm();
    this.createEmailForm();
    this.createKilnSecurityForm();
    this.buttonText.emit({submitButton: "Create"});
    if (this.selected_tab === 'notifications') {
      this.syncNotificationSectionsVisibility();
      this.syncSecondaryPhoneVisibility();
      this.onPushTabSelected();
      if (options?.notificationSection) {
        this.startEditingNotificationSection(options.notificationSection);
      } else {
        this.syncPendingVerificationEditMode();
      }
    }
  }

  getPhoneInformation(is_secondary = false) {
    let default_phone = {call_number: "", type: "Home"};
    let is_empty = (phone) => {
      return phone ? (!phone.carrier && !phone.type && !phone.call_number) : true;
    }

    if(this.selected_user) {
      let secondary = is_empty(this.selected_user.secondary_phone) ? default_phone : this.selected_user.secondary_phone;
      let primary = is_empty(this.selected_user.primary_phone) ? default_phone : this.selected_user.primary_phone;

      return is_secondary ? secondary : primary;
    } else {
      return default_phone;
    }
  }

  normalizeAlertsEnabled(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  isPhoneVerified(phone: { verified?: boolean; is_verified?: boolean } | null | undefined): boolean {
    return !!(phone?.verified || phone?.is_verified);
  }

  /** Server set verification_code when a code was sent but the number is not verified yet. */
  isPhoneVerificationPending(phone: { verification_code?: unknown } | null | undefined): boolean {
    return !!phone?.verification_code;
  }

  shouldShowPhoneVerification(phone: { call_number?: string; verified?: boolean; is_verified?: boolean } | null | undefined): boolean {
    return !!(phone?.call_number?.trim() && !this.isPhoneVerified(phone));
  }

  onPhoneAlertsChanged(): void {
    this.phonesForm?.markAsTouched();
    this.phonesForm?.markAsDirty();
  }

  private matchesUserRecord(
    user: { id?: number; username?: string; email?: string; mail_to?: string },
    ref: { id?: number; username?: string; email?: string }
  ): boolean {
    if (!user || !ref) {
      return false;
    }
    if (ref.id != null && user.id === ref.id) {
      return true;
    }
    if (ref.username && user.username === ref.username) {
      return true;
    }
    if (ref.email && (user.email === ref.email || user.mail_to === ref.email)) {
      return true;
    }
    return false;
  }

  private applyPhoneUpdateToUser(user: { primary_phone?: any; secondary_phone?: any }, response: { phones?: any[] }): void {
    if (!response?.phones) {
      return;
    }
    user.primary_phone = response.phones.find((p) => !p.secondary) ?? user.primary_phone;
    user.secondary_phone = response.phones.find((p) => p.secondary) ?? user.secondary_phone;
  }

  toggleSecondaryPhoneNumber(): void {
    this.showSecondaryPhoneNumber = !this.showSecondaryPhoneNumber;
  }

  togglePrimaryPhoneNumber(): void {
    this.showPrimaryPhoneNumber = !this.showPrimaryPhoneNumber;
  }

  togglePushNotificationsSection(): void {
    this.showPushNotificationsSection = !this.showPushNotificationsSection;
  }

  get useFocusedNotificationEditing(): boolean {
    return this.small_screen && this.selected_tab === 'notifications';
  }

  get showNotificationSectionList(): boolean {
    return this.useFocusedNotificationEditing && this.editingNotificationSection === null;
  }

  shouldShowNotificationSection(section: 'push' | 'primary_phone' | 'secondary_phone'): boolean {
    if (!this.useFocusedNotificationEditing) {
      return true;
    }
    return this.editingNotificationSection === section;
  }

  startEditingNotificationSection(section: 'push' | 'primary_phone' | 'secondary_phone'): void {
    this.editingNotificationSection = section;
    if (section === 'push') {
      this.showPushNotificationsSection = true;
    } else if (section === 'primary_phone') {
      this.showPrimaryPhoneNumber = true;
    } else {
      this.showSecondaryPhoneNumber = true;
    }
  }

  cancelEditingNotificationSection(): void {
    this.editingNotificationSection = null;
    this.syncNotificationSectionsVisibility();
    this.syncSecondaryPhoneVisibility();
  }

  getPushSectionSummaryKey(): string {
    if (!this.accountPushEnabled) {
      return 'USERS.PUSH_DISABLED_FOR_ACCOUNT';
    }
    const count = this.otherUserPushDevices?.length ?? 0;
    if (count === 0) {
      return 'USERS.PUSH_NO_DEVICES_FOR_USER';
    }
    return count === 1 ? 'USERS.PUSH_SECTION_SUMMARY_ONE' : 'USERS.PUSH_SECTION_SUMMARY_MANY';
  }

  getPushSectionSummaryParams(): { count?: number } {
    const count = this.otherUserPushDevices?.length ?? 0;
    return count > 1 ? { count } : {};
  }

  getPrimaryPhoneSummaryKey(): string {
    const phone = this.getPhoneInformation(false);
    if (!phone?.call_number?.trim()) {
      return 'USERS.PHONE_NOT_SET';
    }
    if (phone.verification_code) {
      return 'USERS.PHONE_VERIFICATION_PENDING';
    }
    if (phone.verified) {
      return 'USERS.PHONE_SUMMARY_VERIFIED';
    }
    return 'USERS.PHONE_SUMMARY_UNVERIFIED';
  }

  getSecondaryPhoneSummaryKey(): string {
    const phone = this.getPhoneInformation(true);
    if (!phone?.call_number?.trim()) {
      return 'USERS.PHONE_NOT_SET';
    }
    if (phone.verification_code) {
      return 'USERS.PHONE_VERIFICATION_PENDING';
    }
    if (phone.verified) {
      return 'USERS.PHONE_SUMMARY_VERIFIED';
    }
    return 'USERS.PHONE_SUMMARY_UNVERIFIED';
  }

  getPhoneSummaryParams(isSecondary: boolean): { phone?: string } {
    const phone = this.getPhoneInformation(isSecondary);
    return phone?.call_number?.trim() ? { phone: phone.call_number } : {};
  }

  private syncPendingVerificationEditMode(): void {
    if (!this.useFocusedNotificationEditing || this.editingNotificationSection) {
      return;
    }
    if (this.selected_user?.primary_phone?.verification_code) {
      this.startEditingNotificationSection('primary_phone');
      return;
    }
    if (this.selected_user?.secondary_phone?.verification_code) {
      this.startEditingNotificationSection('secondary_phone');
    }
  }

  private syncNotificationSectionsVisibility(): void {
    const primary = this.getPhoneInformation(false);
    this.showPrimaryPhoneNumber = !!(primary?.call_number?.trim());

    const hasPushDevices = (this.otherUserPushDevices?.length ?? 0) > 0;
    if (this.showPushNotifications && this.accountSmsEnabled && hasPushDevices) {
      this.showPushNotificationsSection = false;
    } else if (this.showPushNotifications) {
      this.showPushNotificationsSection = true;
    }
  }

  private syncSecondaryPhoneVisibility(): void {
    const secondary = this.getPhoneInformation(true);
    this.showSecondaryPhoneNumber = !!(secondary?.call_number?.trim());
  }

  allowUsersChange(event) {
    this.allowUsers = event.target.value;
    if(this.allowUsers === 'all') {
      this.createKilnSecurityForm();
    }
  }

  isSelectedUserCurrentUser(): boolean {
    if (!this.selected_user) return false;
    const email = this.selected_user.email || this.selected_user.username;
    return !!email && (email === this.loggedInUser);
  }

  isLoggedInUser(user: { email?: string; username?: string }): boolean {
    if (!user || !this.loggedInUser) return false;
    const id = user.email || user.username;
    return !!id && id === this.loggedInUser;
  }

  get isOnlyUserOnAccount(): boolean {
    const users = this.accountInformation?.users;
    return !!users && users.length === 1;
  }

  get canDeleteSelectedUser(): boolean {
    if (!this.selected_user || !this.canAdminister || this.isAccountOwner(this.selected_user)) {
      return false;
    }
    return !(this.isOnlyUserOnAccount && this.isSelectedUserCurrentUser());
  }

  isAccountOwner(user: { is_owner?: boolean; type?: string; email?: string; username?: string; mail_to?: string } | null | undefined): boolean {
    if (!user) {
      return false;
    }
    if (user.is_owner || user.type === 'owner') {
      return true;
    }
    const owner = this.accountInformation?.account_owner;
    if (!owner) {
      return false;
    }
    const userIdentity = (user.email || user.mail_to || user.username || '').trim().toLowerCase();
    const ownerIdentity = (owner.email || owner.username || '').trim().toLowerCase();
    return !!userIdentity && !!ownerIdentity && userIdentity === ownerIdentity;
  }

  getSelectedUserDisplayName(): string {
    if (!this.selected_user) return '';
    const name = [this.selected_user.first_name, this.selected_user.last_name].filter(Boolean).join(' ');
    const email = this.selected_user.email || this.selected_user.username || '';
    return name ? `${name} (${email})` : email;
  }

  openUserSelect(): void {
    if (this.userSelect && (!this.filteredUsers || this.filteredUsers.length === 0)) return;
    this.userSelect?.open();
  }

  get isPushAvailable(): boolean {
    return this.pushNotificationService.isPushAvailable;
  }

  get currentPushDeviceName(): string | null {
    return this.pushNotificationService.getCurrentDeviceName();
  }

  /** Tab options for the small-screen dropdown (same visibility as segment buttons). */
  getAvailableTabs(): { value: string; labelKey: string }[] {
    const tabs: { value: string; labelKey: string }[] = [
      { value: 'details', labelKey: 'USERS.USER_DETAILS' },
    ];
    if (this.selected_user && this.canAdminister) {
      tabs.push({ value: 'security', labelKey: 'USERS.SECURITY' });
    }
    if (this.isPremiumAccount() && this.showPushNotifications && this.selected_user && (this.isSelectedUserCurrentUser() || this.canAdminister)) {
      tabs.push({ value: 'notifications', labelKey: 'USERS.NOTIFICATIONS' });
    }
    return tabs;
  }

  onTabDropdownChange(value: string) {
    this.selected_tab = this.normalizeTabName(value) as 'details' | 'security' | 'notifications';
    if (this.selected_tab === 'notifications') {
      this.onNotificationsTabSelected();
    }
  }

  onNotificationsTabSelected() {
    this.selected_tab = 'notifications';
    this.editingNotificationSection = null;
    this.syncNotificationSectionsVisibility();
    this.syncSecondaryPhoneVisibility();
    this.onPushTabSelected();
    this.syncPendingVerificationEditMode();
  }

  onPushTabSelected() {
    if (this.isSelectedUserCurrentUser()) {
      void this.initializePushDeviceNameForCurrentUser();
      void this.refreshPushSetupStatus();
      this.loadOtherUserPushDevices();
    } else if (this.canAdminister && this.selected_user) {
      this.loadOtherUserPushDevices();
    }
  }

  private async refreshPushSetupStatus(): Promise<void> {
    if (!this.isSelectedUserCurrentUser() || !this.isPushAvailable) {
      this.pushSetupStatus = null;
      this.pushPromptPending = false;
      return;
    }
    try {
      this.pushSetupStatus = await this.pushNotificationService.getPushSetupStatus();
      const promptStatus = await this.alertService.getPushPromptStatus();
      this.pushPromptPending = !!promptStatus?.prompt;
    } catch {
      this.pushSetupStatus = null;
      this.pushPromptPending = false;
    }
  }

  isCurrentDeviceRow(device: { name?: string }): boolean {
    if (!this.isSelectedUserCurrentUser()) {
      return false;
    }
    const localName = this.currentPushDeviceName?.trim();
    const deviceName = (device.name || '').trim();
    return !!localName && localName === deviceName;
  }

  canTestPushDevice(device: { id: number }): boolean {
    return this.isSelectedUserCurrentUser()
      && this.isPushAvailable
      && this.accountPushEnabled
      && !!device?.id;
  }

  formatPushDeviceDate(createdAt?: string): string {
    if (!createdAt) {
      return '';
    }
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString();
  }

  get pushStatusMessageKey(): string | null {
    if (!this.isSelectedUserCurrentUser() || !this.isPushAvailable || !this.pushSetupStatus) {
      return null;
    }
    if (this.pushPromptPending) {
      return 'USERS.PUSH_STATUS_NEEDS_REFRESH';
    }
    if (this.pushSetupStatus.permissionGranted === false) {
      return 'USERS.PUSH_STATUS_PERMISSION_DENIED';
    }
    if (this.pushSetupStatus.androidLocalNotificationsGranted === false) {
      return 'USERS.PUSH_STATUS_ANDROID_LOCAL_DENIED';
    }
    if (this.isDeviceRegisteredOnServer()) {
      return 'USERS.PUSH_STATUS_REGISTERED';
    }
    if (this.pushSetupStatus.localDeviceName) {
      return 'USERS.PUSH_STATUS_NOT_ON_SERVER';
    }
    return 'USERS.PUSH_STATUS_NOT_REGISTERED';
  }

  isDeviceRegisteredOnServer(): boolean {
    return this.pushNotificationService.isDeviceRegisteredOnServer(
      this.otherUserPushDevices,
      this.currentPushDeviceName
    );
  }

  async sendTestPushToDevice(device: { id: number; name?: string }) {
    if (!this.canTestPushDevice(device)) {
      return;
    }
    this.pushTestingDeviceId = device.id;
    try {
      const response = await this.alertService.sendTestPushNotification(device.id);
      const message = this.formatTestPushResult(response);
      const success = (response?.succeeded ?? 0) > 0 && (response?.failed ?? 0) === 0;
      this.showPushDeviceAlert(message, success);
      if (response?.stalePushToken) {
        this.pushPromptPending = true;
      }
    } catch (err: unknown) {
      const apiErrorMessage = this.getPushRegisterErrorMessage(err)
        || this.translate.instant('USERS.PUSH_TEST_FAILED');
      this.showPushDeviceAlert(apiErrorMessage, false);
    } finally {
      this.pushTestingDeviceId = null;
    }
  }

  private formatTestPushResult(body: {
    message?: string;
    stalePushToken?: boolean;
    succeeded?: number;
    failed?: number;
  } | null | undefined): string {
    let message = body?.message || this.translate.instant('USERS.PUSH_TEST_SENT');
    if (body?.stalePushToken) {
      const staleHint = this.translate.instant('USERS.PUSH_TEST_STALE_DEVICE');
      message = message ? `${message} ${staleHint}` : staleHint;
    }
    if (body && (body.failed ?? 0) > 0 && (body.succeeded ?? 0) === 0 && !body.stalePushToken) {
      message = this.translate.instant('USERS.PUSH_TEST_DELIVERY_HINT');
    } else if (body && (body.succeeded ?? 0) > 0) {
      const hint = this.translate.instant('USERS.PUSH_TEST_FOREGROUND_HINT');
      message = `${message} ${hint}`;
    }
    return message;
  }

  private async offerTestPushAfterRegistration(deviceName: string): Promise<void> {
    const matchingDevice = this.otherUserPushDevices.find(
      (device) => (device.name || '').trim() === deviceName.trim()
    );
    if (!matchingDevice) {
      return;
    }
    const alert = await this.alertController.create({
      header: this.translate.instant('USERS.PUSH_TEST_NOW_TITLE'),
      message: this.translate.instant('USERS.PUSH_TEST_NOW_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('USERS.CANCEL'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('USERS.PUSH_SEND_TEST'),
          handler: () => {
            void this.sendTestPushToDevice(matchingDevice);
          },
        },
      ],
    });
    await alert.present();
  }

  private async showPushPermissionDeniedAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('USERS.PUSH_PERMISSION_DENIED_TITLE'),
      message: this.translate.instant('USERS.PUSH_PERMISSION_DENIED_MESSAGE'),
      buttons: [
        {
          text: this.translate.instant('USERS.CANCEL'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('USERS.PUSH_OPEN_SETTINGS'),
          handler: () => {
            void this.pushNotificationService.openNotificationSettings();
          },
        },
      ],
    });
    await alert.present();
  }

  private async initializePushDeviceNameForCurrentUser(): Promise<void> {
    await this.pushNotificationService.ensureDeviceNameLoaded();
    this.pushDeviceName = this.pushNotificationService.suggestDefaultDeviceName();
  }

  async removePushDevice(device: { id: number; name?: string; platform?: string }) {
    if (!device || !device.id || !this.selected_user?.id) {
      return;
    }
    const deviceLabel = (device.name || this.translate.instant('USERS.PUSH_DEVICE')).trim();
    const alert = await this.alertController.create({
      header: this.translate.instant('USERS.DELETE_PUSH_DEVICE_TITLE'),
      message: this.translate.instant('USERS.DELETE_PUSH_DEVICE_MESSAGE', { name: deviceLabel }),
      buttons: [
        { text: this.translate.instant('USERS.CANCEL'), role: 'cancel' },
        {
          text: this.translate.instant('USERS.DELETE_PUSH_DEVICE_CONFIRM'),
          role: 'destructive',
          handler: () => {
            void this.confirmRemovePushDevice(device);
          },
        },
      ],
    });
    await alert.present();
  }

  private async confirmRemovePushDevice(device: { id: number; name?: string; platform?: string }) {
    try {
      const res = await this.alertService.deletePushTokenById(device.id);
      const msg = (res && (res as { message?: string }).message) || this.translate.instant('USERS.PUSH_DEVICE_REMOVED');
      const removedName = (device.name || '').trim();
      const currentName = this.pushNotificationService.getCurrentDeviceName()?.trim();
      if (removedName && currentName && removedName === currentName) {
        this.pushNotificationService.setCurrentDeviceName(null);
      }
      this.pending.emit({ is_pending: false, message: msg, success: true });
      this.showPushDeviceAlert(msg, true);
      this.loadOtherUserPushDevices();
      if (this.isSelectedUserCurrentUser()) {
        await this.initializePushDeviceNameForCurrentUser();
      }
    } catch (err: unknown) {
      const apiErrorMessage = this.getPushRegisterErrorMessage(err) || this.translate.instant('USERS.PUSH_DEVICE_REMOVE_FAILED');
      this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
      this.showPushDeviceAlert(apiErrorMessage, false);
    }
  }

  loadOtherUserPushDevices(): Promise<void> {
    const accountId = this.accountService.getCurrentAccount() ?? this.accountInformation?.general?.external_id;
    if (!accountId || !this.selected_user?.id) {
      this.otherUserPushDevices = [];
      this.canRegisterAdditionalPushDevice = false;
      return Promise.resolve();
    }
    this.otherUserPushLoading = true;
    this.otherUserPushDevices = [];
    this.canRegisterAdditionalPushDevice = false;
    return this.alertService.getPushTokensForUser(Number(accountId), this.selected_user.id)
      .then((list) => {
        this.otherUserPushDevices = Array.isArray(list) ? list : [];
        // For the current user, determine if this device can register an additional push token.
        if (this.isSelectedUserCurrentUser()) {
          const currentName = (this.currentPushDeviceName || '').trim();
          const hasCurrentByName = !!currentName && this.otherUserPushDevices.some((d) => (d.name || '').trim() === currentName);
          this.canRegisterAdditionalPushDevice = this.isPushAvailable && this.accountPushEnabled && !hasCurrentByName;
        } else {
          this.canRegisterAdditionalPushDevice = false;
        }
      })
      .catch(() => {
        this.otherUserPushDevices = [];
        if (this.isSelectedUserCurrentUser()) {
          // If we couldn't load existing devices, still allow registration when push is available.
          this.canRegisterAdditionalPushDevice = this.isPushAvailable && this.accountPushEnabled;
        } else {
          this.canRegisterAdditionalPushDevice = false;
        }
      })
      .finally(() => {
        this.otherUserPushLoading = false;
        if (this.selected_tab === 'notifications') {
          this.syncNotificationSectionsVisibility();
          if (this.isSelectedUserCurrentUser()) {
            void this.refreshPushSetupStatus();
          }
        }
      });
  }

  promptUserForPushNotifications() {
    const accountId = this.accountService.getCurrentAccount() ?? this.accountInformation?.general?.external_id;
    if (!accountId || !this.selected_user?.id) {
      this.pending.emit({ is_pending: false, message: 'Cannot send prompt.', success: false });
      return;
    }
    this.promptPushSending = true;
    this.alertService.promptUserForPush(Number(accountId), this.selected_user.email)
      .then((res) => {
        const msg = (res && (res as { message?: string }).message) || 'Prompt sent.';
        this.pending.emit({ is_pending: false, message: msg, success: true });
        this.showPushDeviceAlert(msg, true);
      })
      .catch((err: unknown) => {
        const apiErrorMessage = this.getPushRegisterErrorMessage(err) || 'Failed to send prompt.';
        this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
        this.showPushDeviceAlert(apiErrorMessage, false);
      })
      .finally(() => {
        this.promptPushSending = false;
      });
  }

  async registerPushDevice() {
    const name = (this.pushDeviceName || '').trim();
    if (!name) {
      const message = this.translate.instant('USERS.DEVICE_NAME_REQUIRED');
      this.pending.emit({ is_pending: false, message, success: false });
      this.showPushDeviceAlert(message, false);
      return;
    }
    if (!this.accountPushEnabled) {
      const message = this.translate.instant('USERS.PUSH_DISABLED_FOR_ACCOUNT');
      this.pending.emit({ is_pending: false, message, success: false });
      this.showPushDeviceAlert(message, false);
      return;
    }
    if (!this.pushNotificationService.isPushAvailable) {
      const message = this.translate.instant('USERS.PUSH_NOT_AVAILABLE_ON_DEVICE');
      this.pending.emit({ is_pending: false, message, success: false });
      this.showPushDeviceAlert(message, false);
      return;
    }

    this.pushRegistering = true;
    try {
      const result = await this.pushNotificationService.requestPermissionAndRegister();
      if (result.status === 'denied') {
        this.pending.emit({ is_pending: false, message: this.translate.instant('USERS.PUSH_PERMISSION_DENIED_MESSAGE'), success: false });
        await this.showPushPermissionDeniedAlert();
        return;
      }
      if (result.status !== 'success' || !result.token) {
        const message = this.translate.instant('USERS.PUSH_REGISTER_FAILED');
        this.pending.emit({ is_pending: false, message, success: false });
        this.showPushDeviceAlert(message, false);
        return;
      }
      try {
        await this.alertService.registerPushToken(result.token, name, result.platform);
        this.pushNotificationService.setCurrentDeviceName(name);
        const successMessage = this.translate.instant('USERS.PUSH_DEVICE_REGISTERED_SUCCESS');
        this.pending.emit({ is_pending: false, message: successMessage, success: true });
        this.showPushDeviceAlert(successMessage, true);
        await this.loadOtherUserPushDevices();
        await this.refreshPushSetupStatus();
        await this.offerTestPushAfterRegistration(name);
      } catch (err: unknown) {
        const apiErrorMessage = this.getPushRegisterErrorMessage(err) || this.translate.instant('USERS.PUSH_REGISTER_FAILED');
        const logPayload = this.getPushRegisterErrorLogPayload(err);
        const status = logPayload.status;
        this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
        this.showPushDeviceAlert(status != null ? `${apiErrorMessage} (Status: ${status})` : apiErrorMessage, false);
      }
    } finally {
      this.pushRegistering = false;
    }
  }

  /**
   * Build a plain object from a push register error for logging.
   * HttpErrorResponse stringifies as {} so we copy out status, error body, message explicitly.
   */
  private getPushRegisterErrorLogPayload(err: unknown): { status?: number; error?: unknown; message?: string } {
    if (err == null || typeof err !== 'object') return { message: String(err) };
    const e = err as Record<string, unknown>;
    return {
      status: typeof e['status'] === 'number' ? e['status'] : undefined,
      error: e['error'],
      message: typeof e['message'] === 'string' ? e['message'] : undefined,
    };
  }

  /**
   * Extract a user-facing message from a push register API error.
   * HttpClient rejects with HttpErrorResponse: .error is the response body, .status is the code.
   * JSON.stringify(HttpErrorResponse) can be "{}" so we read properties directly.
   */
  private getPushRegisterErrorMessage(err: unknown): string {
    const fallback = this.translate.instant('USERS.PUSH_REGISTER_FAILED');
    const unavailable = this.translate.instant('USERS.PUSH_API_UNAVAILABLE');
    if (err == null) return fallback;
    if (typeof err === 'object') {
      const e = err as Record<string, unknown>;
      const status = typeof e['status'] === 'number' ? e['status'] : undefined;
      if (status === 404 || status === 503) {
        return unavailable;
      }
      const body = e['error'];
      if (body != null) {
        if (typeof body === 'string') {
          if (this.isNonJsonApiErrorMessage(body)) {
            return unavailable;
          }
          return body || fallback;
        }
        if (typeof body === 'object' && body !== null && typeof (body as Record<string, unknown>)['message'] === 'string') {
          const message = (body as { message: string }).message;
          if (this.isNonJsonApiErrorMessage(message)) {
            return unavailable;
          }
          return message;
        }
      }
      if (typeof e['message'] === 'string' && e['message']) {
        if (this.isNonJsonApiErrorMessage(e['message'] as string)) {
          return unavailable;
        }
        return e['message'] as string;
      }
    }
    if (typeof err === 'string') {
      if (this.isNonJsonApiErrorMessage(err)) {
        return unavailable;
      }
      return err || fallback;
    }
    return fallback;
  }

  private isNonJsonApiErrorMessage(message: string): boolean {
    const normalized = message.trim();
    return /Unexpected token '<'|<!DOCTYPE|<html|is not valid JSON|Http failure during parsing/i.test(normalized)
      || normalized === 'Service unavailable';
  }

  private async showPushDeviceAlert(message: string, success: boolean) {
    try {
      const alert = await this.alertController.create({
        header: this.translate.instant('USERS.PUSH_ALERT_HEADER'),
        message,
        buttons: [this.translate.instant('USERS.OK')],
        cssClass: success ? 'push-alert-success' : 'push-alert-error',
      });
      await alert.present();
    } catch (e) {
      console.warn('Failed to present push device alert', e);
    }
  }

  public save() {
    if (this.smallScreenAwaitingUserPick) {
      return;
    }
    const tabToSave = this.selected_tab;
    this.updatePending = true;
    if(tabToSave === 'details') {
      let formValue = this.usersForm.value
      formValue.enabled = true;
      if(formValue.email && this.selected_user_email && (formValue.email.trim() === this.selected_user_email.trim())) {
        delete formValue.email;
      } else {
        this.is_changing_email = true;
      }

      this.userService.premiumUser(formValue, this.selected_user, this.accountInformation.general.external_id)
          .then((response) => {
            if(this.selected_user) {
              if(this.is_changing_email && !this.userService.isLoggedInAsAnotherUser()) {
                this.userService.tryLogOut();
                this.kilnService.logOut();
                this.navCtrl.navigateForward('/');

                this.getUserAndDeviceInformation();
                this.pending.emit({
                  is_pending: false,
                  message: 'Your information was updated. Please log in with your updated email address.',
                  success: true,
                  dismiss: true
                });
              } else {
                this.updatePending = false;
                void this.showSaveFeedback('User successfully updated.', true);

                if(this.is_changing_email) {
                  this.gettingUserData = true;
                  this.getAccountUsers();
                }

                this.queueSmallScreenReselect(response, this.selected_tab);
                this.getUserAndDeviceInformation();
              }
            } else {
              this.updatePending = false;
              this.isAddingNewUser = false;
              this.clearAddUserQueryParam();
              void this.showSaveFeedback('User successfully created.', true);

              this.queueSmallScreenReselect(response, 'details');
              this.getUserAndDeviceInformation();
              this.accountInformation.users.push(response);
            }
          })
          .catch(() => {
            this.updatePending = false;
            if(this.selected_user) {
              void this.showSaveFeedback('Sorry, we could not update the user at this time', false);
            } else {
              void this.showSaveFeedback('Sorry, we could not create the user at this time', false);
            }
          })

    } else if(tabToSave === 'security' && this.canAdminister) {
      let formValue = this.kilnSecurityForm.value;
      let network_devices = [];
      let kilns = [];
      // Start from the user's can_stop_kilns array (from API), then apply form checkbox changes
      let can_stop_kiln_ids: number[] = [];
      if(this.selected_user && this.selected_user.can_stop_kilns && Array.isArray(this.selected_user.can_stop_kilns)) {
        can_stop_kiln_ids = this.selected_user.can_stop_kilns
          .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
          .filter((id) => !isNaN(id) && id > 0);
      }

      let can_program_kiln_ids: number[] = [];
      if(this.selected_user && this.selected_user.can_program_kilns && Array.isArray(this.selected_user.can_program_kilns)) {
        can_program_kiln_ids = this.selected_user.can_program_kilns
          .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
          .filter((id) => !isNaN(id) && id > 0);
      }

      if(formValue.network_devices && Array.isArray(formValue.network_devices)) {
        formValue.network_devices.forEach((networkDevice) => {
          // Collect network devices for view access - validate ID is a positive number
          if(networkDevice.can_view && networkDevice.id) {
            let deviceId = typeof networkDevice.id === 'string' ? parseInt(networkDevice.id) : networkDevice.id;
            // Only add valid positive IDs (exclude -1, "na", 0, null, etc.)
            if(!isNaN(deviceId) && deviceId > 0) {
              network_devices.push(deviceId);
            }
          }

          // Collect kilns for view; apply form can_stop and can_program changes
          if(networkDevice.kilns && Array.isArray(networkDevice.kilns)) {
            networkDevice.kilns.forEach((kiln) => {
              if(kiln.can_view && kiln.id) {
                let kilnId = typeof kiln.id === 'string' ? parseInt(kiln.id) : kiln.id;
                // Only add valid positive IDs
                if(!isNaN(kilnId) && kilnId > 0) {
                  kilns.push(kilnId);
                }
              }
              const kilnId = typeof kiln.id === 'string' ? parseInt(kiln.id) : kiln.id;
              if(isNaN(kilnId) || kilnId <= 0) return;
              if(kiln.can_stop) {
                if(!can_stop_kiln_ids.includes(kilnId)) can_stop_kiln_ids.push(kilnId);
              } else {
                can_stop_kiln_ids = can_stop_kiln_ids.filter((id) => id !== kilnId);
              }
              if(kiln.can_program) {
                if(!can_program_kiln_ids.includes(kilnId)) can_program_kiln_ids.push(kilnId);
              } else {
                can_program_kiln_ids = can_program_kiln_ids.filter((id) => id !== kilnId);
              }
            });
          }
        });
      }
      
      // Submit network_devices, kilns, can_stop_kilns, and can_program_kilns
      let to_submit: Record<string, unknown> = {  
        network_devices: network_devices,      
        kilns: kilns,
        can_stop_kilns: can_stop_kiln_ids
      };
      if (this.showProgrammingToggle) {
        to_submit.can_program_kilns = can_program_kiln_ids;
      }
      
      this.userService.updateKilnSecurity((this.selected_user && this.selected_user.id), this.accountInformation.general.external_id, to_submit)
          .then((success) => {
            this.updatePending = false;
            console.log(`successfully updated user security`);
            void this.showSaveFeedback('User security updated successfully.', true);
            this.getUserAndDeviceInformation(true);
          })
          .catch((error) => {
            this.updatePending = false;
            console.log(`error updating user security`);
            console.log(error);
            void this.showSaveFeedback("Something went wrong updating the user's kiln access", false);
          });

    } else if(tabToSave === 'phone' || (tabToSave === 'notifications' && this.accountSmsEnabled)) {
      if (!this.accountSmsEnabled) {
        this.updatePending = false;
        return;
      }
      let formValue = this.phonesForm.value;
      let call_numbers = []
      if(formValue.primary_number !== "") {
        if(!formValue.primary_number.includes("+")) { formValue.primary_number = `+${formValue.primary_number}`}
        let primary_number = this.phoneUtil.parseAndKeepRawInput(formValue.primary_number, 'US');
        call_numbers.push({
          call_number: this.phoneUtil.format(primary_number, PhoneNumberFormat.INTERNATIONAL),
          type: formValue.primary_type,
          carrier: "",
          primary: true,
          secondary: false,
          enable_for_alerts: formValue.primary_alerts_enabled
        });
      }

      if(formValue.secondary_number !== "") {
        if(!formValue.secondary_number.includes("+")) { formValue.secondary_number = `+${formValue.secondary_number}`}
        let secondary_number = this.phoneUtil.parseAndKeepRawInput(formValue.secondary_number, 'US');
        call_numbers.push({
          call_number: this.phoneUtil.format(secondary_number, PhoneNumberFormat.INTERNATIONAL),
          type: formValue.secondary_type,
          carrier: "",
          primary: false,
          secondary: true,
          enable_for_alerts: formValue.secondary_alerts_enabled
        })
      }

      if(call_numbers.length === 0) {
        this.updatePending = false;
        void this.showSaveFeedback('Please update a phone number before saving', false);
      } else {
        this.userService.updatePhone(this.selected_user, call_numbers)
            .then((response) => {
              this.updatePending = false;
              this.accountInformation.users = this.accountInformation.users.map((u) => {
                if (this.matchesUserRecord(u, response)) {
                  this.applyPhoneUpdateToUser(u, response);
                }
                return u;
              });
              if (this.selected_user && this.matchesUserRecord(this.selected_user, response)) {
                this.applyPhoneUpdateToUser(this.selected_user, response);
              }
              this.getUserAndDeviceInformation(true);
              void this.showSaveFeedback('Phones updated successfully.', true);
            })
            .catch((error) => {
              this.updatePending = false;
              void this.showSaveFeedback('Something went wrong updating phone numbers', false);
            });
      }
    } else if(tabToSave === 'email') {
      let formValue = this.usersForm.value;
      this.userService.updateEmail(this.selected_user, {email: formValue.email})
          .then((response) => {
            this.updatePending = false;
            this.getUserAndDeviceInformation(true);
            void this.showSaveFeedback('Email updated successfully.', true);
            this.accountInformation.users = this.accountInformation.users.map((u) => {
              if(u.username !== response.username) { u.email = response.email; }
              return u;
            });
          })
          .catch((error) => {
            this.updatePending = false;
            void this.showSaveFeedback('Something went wrong updating the user email', false);
          });
    } else {
      this.updatePending = false;
    }
  }

  public addUser() {
    this.smallScreenAwaitingUserPick = false;
    this.isAddingNewUser = true;
    this.selected_user = null;
    this.selected_index = null;
    this.selected_tab = 'details';
    this.showSecondaryPhoneNumber = false;
    this.showPrimaryPhoneNumber = true;
    this.showPushNotificationsSection = true;
    this.editingNotificationSection = null;
    this.createForm();
    this.createPhonesForm();
    this.createEmailForm();
    this.buttonText.emit({submitButton: "Create"});
    this.ensureAddUserQueryParam();
  }

  /** Keep `add=user` in the URL so Account Admin title/hint stay in create mode. */
  private ensureAddUserQueryParam(): void {
    if (this.urlSaysAddUserIntent()) {
      return;
    }
    this.router.navigate(['/account-admin'], {
      queryParams: { tab: 'users', add: 'user' },
      queryParamsHandling: 'merge',
    });
  }

  async removeUser() {
    if (this.deletePending || !this.canDeleteSelectedUser) {
      return;
    }

    const userToRemove = this.selected_user;
    const userEmail = userToRemove.email || userToRemove.mail_to || userToRemove.username;
    if (!userEmail) {
      void this.showSaveFeedback(this.translate.instant('USERS.DELETE_USER_FAILURE'), false);
      return;
    }

    let username = userEmail || 'this user';
    const alert = await this.alertController.create({
      header: this.translate.instant('USERS.DELETE_USER_CONFIRM_TITLE'),
      message: this.translate.instant('USERS.DELETE_USER_CONFIRM_MESSAGE', { user: username }),
      buttons: [
        {
          text: this.translate.instant('USERS.DELETE_USER_CONFIRM_YES'),
          role: 'delete',
          cssClass: 'primary',
          handler: () => {
            if (this.deletePending) {
              return false;
            }
            this.deletePending = true;
            this.pending.emit({is_pending: true});
            this.userService.removeUserFromAccount(this.accountInformation.general.external_id, userEmail)
                .then((response) => {
                  void this.showSaveFeedback(this.translate.instant('USERS.DELETE_USER_SUCCESS'), true);

                  const removedId = response && response.removed != null ? response.removed : userToRemove.id;
                  this.accountInformation.users = this.accountInformation.users.filter(
                    (u) => u.id !== removedId && u.email !== userEmail && u.username !== userEmail && u.mail_to !== userEmail
                  );
                  this.selected_user = undefined;
                  this.clearCurrentForm();
                })
                .catch((error) => {
                  void this.showSaveFeedback(this.getDeleteUserErrorMessage(error), false);
                })
                .finally(() => {
                  this.deletePending = false;
                });
          }
        }, {
          text: this.translate.instant('USERS.CANCEL'),
          cssClass: 'secondary',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  private getDeleteUserErrorMessage(error: unknown): string {
    const fallback = this.translate.instant('USERS.DELETE_USER_FAILURE');
    if (error == null || typeof error !== 'object') {
      return fallback;
    }
    const e = error as Record<string, unknown>;
    const body = e['error'];
    if (body != null && typeof body === 'object' && typeof (body as { message?: string }).message === 'string') {
      return (body as { message: string }).message || fallback;
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (typeof e['message'] === 'string' && (e['message'] as string).trim()) {
      return e['message'] as string;
    }
    return fallback;
  }

  deletePhone(secondary) {
    let phone = secondary ? (this.selected_user.secondary_phone && this.selected_user.secondary_phone.call_number) : (this.selected_user.primary_phone && this.selected_user.primary_phone.call_number);
    this.alertService.deletePhone(phone)
        .then((success) => {
          this.getAccountUsers();
          this.pending.emit({is_pending: false,message: "The phone number was deleted",success: true});
        })
        .catch((err) => {
          this.pending.emit({is_pending: false,message: "Something went wrong deleting your phone number",success: false});
        })
  }

  verifyPhone(secondary) {
    let val = this.phonesForm.value;
    let call_number = secondary ? val.secondary_number : val.primary_number;
    this.alertService.verifyDevice(this.selected_user, "phone", {phone: call_number})
        .then((response) => {this.verificationSuccess() })
        .catch((error) => { this.verificationFails() });
  }

  confirmPhoneVerification(secondary) {
    let val = this.phonesForm.value;
    let call_number = secondary ? val.secondary_number : val.primary_number;
    this.alertService.confirmVerification("phone", {phone: call_number, verification_code: val.verification_code})
        .then((response) => { this.deviceConfirmSuccess() })
        .catch((error) => { this.deviceConfirmFails() })
  }

  verifyEmail() {
    let val = this.usersForm.value;
    this.alertService.verifyDevice(this.selected_user, "email", {email: val.email})
        .then((response) => { this.verificationSuccess() })
        .catch((error) => { this.verificationFails() })
  }

  confirmEmailVerification() {
    let val = this.usersForm.value;
    this.alertService.confirmVerification("email", {email: val.email, verification_code: val.verification_code})
        .then((response) => { this.deviceConfirmSuccess() })
        .catch((error) => { this.deviceConfirmFails() })
  }

  public pickerButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
    },
    {
      text: 'Confirm',
      handler: (selection) => {
        this.selectUser(selection.users.value.user, selection.users.value.index);
      },
    },
  ];

  onUserSelectChange() {
    if (this.filteredUsers?.length && this.selected_index >= 0 && this.selected_index < this.filteredUsers.length) {
      this.selectUser(this.filteredUsers[this.selected_index], this.selected_index);
    }
  }

  async showPicker() {
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'users',
        options: this.accountInformation.users.sort((u1, u2) => {
          let val = 0;
          if((u1.type === 'admin' && u2.type !== 'admin') || (u1.type === 'basic' && u2.type === 'readonly')) val = -1;
          if(u1.type === 'readonly') val = 1;
          return val;
        }).map((user, i) => { 
          let typeLabels = {'admin': "Admin", 'basic': "Basic", 'readonly': "Readonly"}
          return {          
            text: `${user.email} - ${typeLabels[user.type]}`, value: {user: user, index: i}
          }})
      }],
      buttons: this.pickerButtons
    });

    await picker.present();
  }

  public isValid() {
    if (this.small_screen && this.smallScreenAwaitingUserPick) {
      return false;
    }
    const tab = this.selected_tab;
    if (tab === 'notifications' || tab === 'push') {
      if (!this.accountSmsEnabled || !this.phonesForm) {
        return false;
      }
      return (this.phonesForm.touched || this.phonesForm.dirty) ? this.phonesForm.valid : false;
    }
    if (tab === 'security') {
      return (this.kilnSecurityForm && this.kilnSecurityForm.touched) ? this.kilnSecurityForm.valid : true;
    }
    if (tab) {
      const getFormToCheck = {
        details: this.usersForm,
        phone: this.phonesForm,
        email: this.emailForm,
      };

      const currentForm = getFormToCheck[tab];
      return (currentForm && currentForm.touched) ? currentForm.valid : true;
    }
    return true;
  }

  get filteredUsers() {
    if (!this.accountInformation || !this.accountInformation.users) return [];
    switch (this.selectedRole) {
      case 'admin':
        return this.accountInformation.users.filter(u => u.type === 'admin');
      case 'basic':
        return this.accountInformation.users.filter(u => u.type === 'basic');
      case 'readonly':
        return this.accountInformation.users.filter(u => u.type === 'readonly');
      default:
        return this.accountInformation.users;
    }
  }
}
